import { GiftIcon, GlobeIcon, HandshakeIcon, PassIcon, ShirtIcon } from "@xola/icons";
import { Breadcrumb, HeaderToolbar, Skeleton } from "@xola/ui-kit";
import { useUnmount } from "ahooks";
import { isArray, memoize, range } from "lodash";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { Product } from "types";
import { GiftQrCodeScanner } from "../../components/Common/GiftQrCodeScanner";
import { ColumnLayout } from "../../components/Layout/ColumnLayout";
import { ContentLayout } from "../../components/Layout/ContentLayout";
import { ExperienceList } from "../../components/PurchaseCreate/ExperienceList";
import { GiftOfferList } from "../../components/PurchaseCreate/GiftOfferList";
import { PackageList } from "../../components/PurchaseCreate/PackageList";
import { ResumePurchaseBanner } from "../../components/PurchaseCreate/ResumePurchaseBanner";
import { useMerchandise, useProducts } from "../../hooks/resources/useProduct";
import { useSearch } from "../../hooks/useSearch";
import { Page } from "../Page";
import { ProductFilterDropdown } from "@/components/Product/Filters/ProductFilterDropdown";
import { MembershipList } from "@/components/PurchaseCreate/Membership/MembershipList";
import { MerchandiseList } from "@/components/PurchaseCreate/Merchandise/MerchandiseList";
import { PartnerExperienceList } from "@/components/PurchaseCreate/PartnerExperienceList";
import { PartnerPackageList } from "@/components/PurchaseCreate/PartnerPackageList";
import { useSeller } from "@/hooks/useAccount";
import { useSearchAutoFocus } from "@/hooks/useSearchAutoFocus";
import { useCreatePurchaseStore } from "@/stores/createPurchaseStore";
import { getPermissions } from "@/stores/globalStore";

const LOCAL_STORAGE_KEY = "filteredProductCategories";

export const SelectExperiencePage = () => {
    const queryParams = new URLSearchParams(location.search);
    const productsInCart = useCreatePurchaseStore.getState().getProductsInPurchase();
    const seller = useSeller();

    useUnmount(() => {
        const { organizer, purchase, actions } = useCreatePurchaseStore.getState();
        if (organizer && purchase.items.length === 0) {
            actions.resetState();
        }
    });
    const shouldAutoFocus = useSearchAutoFocus();
    const [filter] = useState(queryParams.get("filter") ?? ProductOption.ALL);
    const permissions = getPermissions();
    const [search, setSearch] = useState(queryParams.get("search") ?? "");
    const history = useHistory<{ isAddingItem?: boolean } | null>();
    const isAddingItem = history.location.state?.isAddingItem === true;
    const filteredOptions = getOptions();

    const [selectedItems, setSelectedItems] = useState<string[]>(() =>
        JSON.parse(
            localStorage.getItem(LOCAL_STORAGE_KEY) ??
                JSON.stringify(filteredOptions.map((option: { id: string }) => option.id)),
        )?.filter((localItem: string) =>
            filteredOptions?.find((filteredItem: { id: string }) => filteredItem.id === localItem),
        ),
    );

    const handleSelectionChange = (items: string[]) => {
        setSelectedItems(items);
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(items));
    };

    const {
        filteredProducts: { experiences = [], memberships = [], giftOffers = [], packages = [] },
        isLoading,
    } = useProducts();

    const { merchandise = [], isLoading: isLoadingMerchandise } = useMerchandise();

    const filtered = useSearch(experiences, (experience) => [experience.name], search);
    const filteredMemberships = useSearch(memberships, (membership) => [membership.name], search);
    const filteredGiftOffers = useSearch(giftOffers, (giftOffer) => [giftOffer.name], search);
    const filteredPackages = useSearch(packages, (pkg) => [pkg.name], search);
    const filteredMerchandise = useSearch(merchandise, (item) => [item.name], search);

    const shouldShowSection = (sectionFilter: ProductOption, products?: Product[]) => {
        // If no product filter options are selected, then show all sections
        const isSectionSelected = selectedItems.includes(String(sectionFilter)) || selectedItems.length === 0;
        const shouldShow = (filter === sectionFilter || filter === ProductOption.ALL) && isSectionSelected;

        if (
            isAddingItem &&
            productsInCart.some((product) => product.seller.id === seller.id) &&
            sectionFilter === ProductOption.PARTNER_EXPERIENCES
        ) {
            // if there's an item from current seller in cart, don't show partner experiences on add another product page.
            // Can be removed when partner mist bookings are implemented in back office
            return false;
        }
        if ([ProductOption.PACKAGES, ProductOption.PARTNER_PACKAGES].includes(sectionFilter) && isAddingItem) {
            // Hide pkgs and partner pkgs from Add another product page
            return false;
        }

        return shouldShow && (isArray(products) ? products.length > 0 : true);
    };

    return (
        <Page title="Select Product">
            <GiftQrCodeScanner />
            <ColumnLayout>
                <HeaderToolbar>
                    <HeaderToolbar.Breadcrumb>
                        <Breadcrumb.Item>Purchases</Breadcrumb.Item>
                        <Breadcrumb.Item>Make a Purchase</Breadcrumb.Item>
                    </HeaderToolbar.Breadcrumb>

                    <HeaderToolbar.Search
                        shouldHideMenu
                        autoFocus={shouldAutoFocus}
                        placeholder="Search by name"
                        className="!text-base"
                        onChange={setSearch}
                    />
                </HeaderToolbar>

                <ColumnLayout.Fill>
                    <ContentLayout isWide>
                        <div className="flex flex-row items-center justify-between">
                            <h2>Products</h2>
                            <ProductFilterDropdown
                                options={filteredOptions}
                                externalSelectedItems={selectedItems}
                                onSelectionChange={handleSelectionChange}
                            />
                        </div>

                        <ResumePurchaseBanner />

                        <div className="divide-y divide-gray-light">
                            {shouldShowSection(ProductOption.EXPERIENCES, experiences) ? (
                                <ExperienceList experiences={filtered} />
                            ) : (
                                isLoading && <ProductSkeleton header="Experiences" />
                            )}

                            {permissions.canViewMemberships &&
                                (shouldShowSection(ProductOption.MEMBERSHIPS, memberships) ? (
                                    <MembershipList memberships={filteredMemberships} />
                                ) : (
                                    isLoading && <ProductSkeleton header="Memberships" />
                                ))}

                            {permissions.canViewGiftOffers &&
                                (shouldShowSection(ProductOption.GIFT_OFFERS, giftOffers) ? (
                                    <GiftOfferList giftOffers={filteredGiftOffers} />
                                ) : (
                                    isLoading && <ProductSkeleton header="Gifts" />
                                ))}

                            {permissions.canViewMerchandise &&
                                (shouldShowSection(ProductOption.MERCHANDISE, merchandise) ? (
                                    <MerchandiseList merchandises={filteredMerchandise} />
                                ) : (
                                    isLoadingMerchandise && <ProductSkeleton header="Merchandise" />
                                ))}

                            {permissions.canViewPackages &&
                                (shouldShowSection(ProductOption.PACKAGES, packages) ? (
                                    <PackageList packages={filteredPackages} experiences={experiences} />
                                ) : (
                                    isLoading && <ProductSkeleton header="Packages" />
                                ))}

                            {permissions.canViewPartnerExperiences &&
                                shouldShowSection(ProductOption.PARTNER_EXPERIENCES) && (
                                    <PartnerExperienceList search={search} />
                                )}

                            {permissions.canViewPartnerPackages &&
                                (shouldShowSection(ProductOption.PARTNER_PACKAGES, experiences) ? (
                                    <PartnerPackageList search={search} />
                                ) : (
                                    isLoading && <ProductSkeleton header="Partner Packages" />
                                ))}
                        </div>
                    </ContentLayout>
                </ColumnLayout.Fill>
            </ColumnLayout>
        </Page>
    );
};

export const ProductSkeleton = ({ header = "Loading" }) => {
    return (
        <div className="pt-10">
            <div className="my-2 text-lg font-bold text-gray-dark">{header}</div>
            <div className="mt-10 grid grid-cols-3 gap-8">
                {range(0, 12).map((key) => (
                    <Skeleton key={key} height={80} />
                ))}
            </div>
        </div>
    );
};

export enum ProductOption {
    ALL = "all",
    EXPERIENCES = "experiences",
    MEMBERSHIPS = "membership",
    GIFT_OFFERS = "gift_offers",
    MERCHANDISE = "merchandise",
    PACKAGES = "packages",
    PARTNER_EXPERIENCES = "partner_experiences",
    PARTNER_PACKAGES = "partner_packages",
}

const getOptions = memoize(() => {
    const permissions = getPermissions();
    const options: any = [
        {
            id: ProductOption.EXPERIENCES,
            label: "Experiences",
            icon: <GlobeIcon />,
            testId: "purchases-make-a-purchase-experiences-checkbox",
        },
    ];
    if (permissions.canViewPackages) {
        options.push({
            id: ProductOption.PACKAGES,
            label: "Packages",
            icon: <HandshakeIcon />,
            testId: "purchases-make-a-purchase-packages-checkbox",
        });
    }

    if (permissions.canViewGiftOffers) {
        options.push({
            id: ProductOption.GIFT_OFFERS,
            label: "Gifts",
            icon: <GiftIcon />,
            testId: "purchases-make-a-purchase-gifts-checkbox",
        });
    }

    if (permissions.canViewMemberships) {
        options.push({
            id: ProductOption.MEMBERSHIPS,
            label: "Memberships",
            icon: <PassIcon />,
            testId: "purchases-make-a-purchase-membership-checkbox",
        });
    }

    if (permissions.canViewMerchandise) {
        options.push({
            id: ProductOption.MERCHANDISE,
            label: "Merchandise",
            icon: <ShirtIcon />,
            testId: "purchases-make-a-purchase-merchandise-checkbox",
        });
    }

    if (permissions.canViewPartnerExperiences) {
        options.push({
            id: ProductOption.PARTNER_EXPERIENCES,
            label: "Partner Experiences",
            icon: <HandshakeIcon />,
            category: "Partners Products",
            testId: "purchases-make-a-purchase-partner-experiences-checkbox",
        });
    }

    if (permissions.canViewPartnerPackages) {
        options.push({
            id: ProductOption.PARTNER_PACKAGES,
            label: "Partner Packages",
            icon: <HandshakeIcon />,
            category: "Partners Products",
            testId: "purchases-make-a-purchase-partner-packages-checkbox",
        });
    }

    return options;
});
